import React, { useState, useEffect } from 'react';
import { ArrowsVertical } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TimelineNavigationProps {
  questions: string[];
  activeIndex: number;
  onNavigate: (index: number) => void;
  offset: number;
}

export function TimelineNavigation({ questions, activeIndex, onNavigate, offset }: TimelineNavigationProps) {
  const [prevActiveIndex, setPrevActiveIndex] = useState(activeIndex);

  useEffect(() => {
    setPrevActiveIndex(activeIndex);
  }, [activeIndex]);

  // Show 2 items before and after the active item
  const VISIBLE_ITEMS_EACH_SIDE = 2;
  const totalItems = questions.length;

  const getVisibleIndexes = () => {
    const indexes: (number | 'ellipsis')[] = [];
    let lastAddedIndex: number | null = null;

    const addIndex = (index: number) => {
      if (lastAddedIndex !== null && index - lastAddedIndex > 1) {
        indexes.push('ellipsis');
      }
      indexes.push(index);
      lastAddedIndex = index;
    };

    // Always show first item
    addIndex(0);

    // Show items around active index
    for (
      let i = Math.max(1, activeIndex - VISIBLE_ITEMS_EACH_SIDE);
      i <= Math.min(totalItems - 2, activeIndex + VISIBLE_ITEMS_EACH_SIDE);
      i++
    ) {
      addIndex(i);
    }

    // Always show last item if we have more than one item
    if (totalItems > 1) {
      addIndex(totalItems - 1);
    }

    return indexes;
  };

  const visibleIndexes = getVisibleIndexes();

  return (
    <nav
      aria-label="Question timeline navigation"
      className="fixed left-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3"
      style={{ ...(offset ? { left: `${offset}px` } : {}) } as React.CSSProperties}
    >
      <motion.div
        className="flex flex-col items-center space-y-3"
        role="list"
        initial={false}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <AnimatePresence>
          {visibleIndexes.map((indexOrEllipsis, arrayIndex) => {
            if (indexOrEllipsis === 'ellipsis') {
              return (
                <motion.div
                  key={`ellipsis-${arrayIndex}`}
                  className="h-8 flex items-center justify-center"
                  aria-hidden="true"
                  initial={{ opacity: 0, y: prevActiveIndex > activeIndex ? -20 : 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: prevActiveIndex > activeIndex ? 20 : -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowsVertical className="size-4 text-foreground" />
                </motion.div>
              );
            }

            const index = indexOrEllipsis;
            const question = questions[index];

            return (
              <motion.div
                key={index}
                className="relative group isolate"
                role="listitem"
                initial={{ opacity: 0, y: prevActiveIndex > activeIndex ? -20 : 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: prevActiveIndex > activeIndex ? 20 : -20 }}
                transition={{ duration: 0.2 }}
                layout
              >
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onNavigate(index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            onNavigate(index);
                          }
                        }}
                        className={cn(
                          'flex items-center justify-center size-8 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:opacity-90 cursor-pointer ring-primary ',
                          index === activeIndex ? 'bg-primary text-primary-foreground' : 'bg-stone-300 text-zinc-800',
                        )}
                        aria-label={`Navigate to question ${index + 1}: ${question}`}
                        aria-current={index === activeIndex ? 'step' : undefined}
                      >
                        {index + 1}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      sideOffset={8}
                      bgColor="--bfc-card"
                      className="max-w-[300px] text-card-foreground border-foreground/35"
                    >
                      <p className="break-words leading-relaxed">{question}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </nav>
  );
}
